import { ArrowDown2 } from '@/components/icons';
import { Checkbox } from '@/components/ui/checkbox';
import { controlVariants } from '@/components/ui/control-variants';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import FieldLabel from './FieldLabel';

interface YColumnListProps {
    columns: { id: string; label: string }[];
    selectedIds: string[];
    defaultYAxisColumnId?: string | null;
    /** 2+ columns allowed (each becomes a series) vs exactly one. */
    multiSeries: boolean;
    disabled?: boolean;
    onToggle: (columnId: string) => void;
}

const SuggestedPill = () => (
    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground">
        Sugerido
    </span>
);

/** Comma-joined selected labels, truncated to `+N` past the first two. */
function summarizeSelection(labels: string[]): string {
    if (labels.length === 0) return '';
    if (labels.length <= 2) return labels.join(', ');
    return `${labels.slice(0, 2).join(', ')} +${labels.length - 2}`;
}

/**
 * Y-axis column selector. Multi-series charts get a compact popover checkbox
 * list (a Select-styled trigger that expands on demand); single-series charts
 * get a plain Select. Single vs multi is driven by the chart type's
 * `multiSeries` flag via the parent's `onToggle` handler — this component only
 * renders the control.
 */
export default function YColumnList({
    columns,
    selectedIds,
    defaultYAxisColumnId,
    multiSeries,
    disabled = false,
    onToggle,
}: YColumnListProps) {
    const label = multiSeries ? 'Colunas do eixo Y' : 'Coluna do eixo Y';

    if (!multiSeries) {
        return (
            <div>
                <FieldLabel hint="Selecione a coluna numérica que será representada.">
                    {label}
                </FieldLabel>
                <Select value={selectedIds[0] ?? ''} onValueChange={onToggle} disabled={disabled}>
                    <SelectTrigger>
                        <SelectValue placeholder="Selecione a coluna" />
                    </SelectTrigger>
                    <SelectContent>
                        {columns.map((column) => (
                            <SelectItem key={`y-${column.id}`} value={column.id}>
                                <div className="flex w-full items-center justify-between gap-2">
                                    <span>{column.label}</span>
                                    {column.id === defaultYAxisColumnId && <SuggestedPill />}
                                </div>
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
        );
    }

    const selectedLabels = columns
        .filter((column) => selectedIds.includes(column.id))
        .map((column) => column.label);
    const summary = summarizeSelection(selectedLabels);

    return (
        <div>
            <FieldLabel hint="Selecione uma ou mais colunas numéricas. Cada coluna vira uma série.">
                {label}
            </FieldLabel>
            <Popover modal>
                <PopoverTrigger asChild>
                    <button
                        type="button"
                        disabled={disabled}
                        className={cn(
                            controlVariants(),
                            'items-center justify-between whitespace-nowrap focus:ring-2 focus:ring-ring/20',
                        )}
                    >
                        <span
                            className={cn(
                                'line-clamp-1 text-left',
                                !summary && 'text-muted-foreground',
                            )}
                        >
                            {summary || 'Selecione as colunas'}
                        </span>
                        <ArrowDown2 className="ml-2 size-4 shrink-0 opacity-50" />
                    </button>
                </PopoverTrigger>
                <PopoverContent
                    align="start"
                    className="max-h-64 w-[var(--radix-popover-trigger-width)] overflow-y-auto p-1"
                >
                    {columns.map((column) => {
                        const checked = selectedIds.includes(column.id);
                        return (
                            <label
                                key={`y-${column.id}`}
                                className={cn(
                                    'flex w-full cursor-pointer items-center justify-between gap-2 rounded-md p-2 text-left text-sm transition-colors',
                                    checked ? 'text-blue-700' : 'text-foreground',
                                    'hover:bg-surface',
                                )}
                            >
                                <span className="flex items-center gap-2.5">
                                    <Checkbox
                                        checked={checked}
                                        onCheckedChange={() => onToggle(column.id)}
                                    />
                                    {column.label}
                                </span>
                                {column.id === defaultYAxisColumnId && <SuggestedPill />}
                            </label>
                        );
                    })}
                </PopoverContent>
            </Popover>
        </div>
    );
}
